/**
 * OwnershipReviewedBadge — buyer-facing "Ownership documents reviewed" badge.
 *
 * Rendered on the public listing detail page and on search result cards when
 * the public house payload has `ownership_documents_reviewed: true` AND an
 * `ownership_reviewed_month` that formats (the review date is required).
 * Renders NOTHING otherwise — there is deliberately no "unverified" state (decision
 * D2, legal-review.md §4).
 *
 * Interaction: a toggletip. The badge is a real <button> (keyboard + touch);
 * activating it opens a popover with the verbatim legal copy. The popover is
 * portaled to <body> with fixed positioning because search cards clip their
 * overflow. Escape, the close button, a click outside, or tabbing away
 * closes it and returns focus to the badge.
 *
 * @param {object} props
 * @param {boolean} props.reviewed - `ownership_documents_reviewed`
 * @param {string|null} [props.reviewedMonth] - `ownership_reviewed_month` ("YYYY-MM")
 * @param {'sm'|'md'} [props.size='md']
 * @param {string} [props.className]
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { LEGAL_DISCLAIMER } from '../../utils/disclaimers';
import {
  BADGE_LABEL,
  BADGE_TOOLTIP_P1,
  BADGE_TOOLTIP_P2,
  formatReviewedMonth,
} from '../../utils/ownershipVerification';
import styles from './OwnershipReviewedBadge.module.css';

const VIEWPORT_MARGIN = 8;
const POPOVER_MAX_WIDTH = 360;
const GAP = 8;

function computePosition(anchor, popover) {
  const rect = anchor.getBoundingClientRect();
  const vw = window.innerWidth || document.documentElement.clientWidth || 1024;
  const vh = window.innerHeight || document.documentElement.clientHeight || 768;
  const width = Math.min(POPOVER_MAX_WIDTH, vw - VIEWPORT_MARGIN * 2);
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(rect.left, vw - width - VIEWPORT_MARGIN),
  );
  const height = popover ? popover.offsetHeight : 0;
  const spaceBelow = vh - rect.bottom;
  // Prefer below; flip above when it would run off-screen and there's more room there.
  if (height && spaceBelow < height + GAP && rect.top > spaceBelow) {
    return { left, width, bottom: vh - rect.top + GAP };
  }
  return { left, width, top: rect.bottom + GAP };
}

export default function OwnershipReviewedBadge({
  reviewed,
  reviewedMonth = null,
  size = 'md',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const uid = useId();
  const popoverId = `${uid}-ownership-popover`;
  const titleId = `${uid}-ownership-title`;

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus && buttonRef.current) buttonRef.current.focus();
  }, []);

  const reposition = useCallback(() => {
    if (!buttonRef.current) return;
    setPosition(computePosition(buttonRef.current, popoverRef.current));
  }, []);

  // Position before paint, then again once the popover has a measurable height.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return undefined;
    if (popoverRef.current) popoverRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      }
    };
    const onPointer = (e) => {
      const t = e.target;
      if (popoverRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, close, reposition]);

  // The review date is required (legal, 2026-09-29): no month, no badge.
  const month = formatReviewedMonth(reviewedMonth);
  if (reviewed !== true || !month) return null;

  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((o) => !o);
  };

  // Tabbing out of the popover closes it (focus is leaving the toggletip).
  const handleBlur = (e) => {
    const next = e.relatedTarget;
    if (!next) return;
    if (popoverRef.current?.contains(next) || buttonRef.current?.contains(next)) return;
    setOpen(false);
  };

  const popoverStyle = position
    ? {
        left: position.left,
        width: position.width,
        ...(position.top != null ? { top: position.top } : { bottom: position.bottom }),
      }
    : { visibility: 'hidden' };

  const popover =
    open && typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            tabIndex={-1}
            className={styles.popover}
            style={popoverStyle}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
            data-testid="ownership-badge-popover"
          >
            <div className={styles.popoverHead}>
              <p id={titleId} className={styles.popoverTitle}>
                <span className={styles.sealSm} aria-hidden="true">✓</span>
                {BADGE_LABEL}
              </p>
              <button
                type="button"
                className={styles.close}
                onClick={() => close(true)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className={styles.text}>
              {BADGE_TOOLTIP_P1} Reviewed {month}.
            </p>
            <p className={styles.text}>
              <strong>{BADGE_TOOLTIP_P2.emphasis1}</strong>
              {BADGE_TOOLTIP_P2.body1}
              <strong>{BADGE_TOOLTIP_P2.emphasis2}</strong>
              {BADGE_TOOLTIP_P2.body2}
            </p>
            <p className={styles.disclaimer}>{LEGAL_DISCLAIMER}</p>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.badge} ${size === 'sm' ? styles.sm : ''} ${className}`.trim()}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-haspopup="dialog"
        onClick={handleToggle}
        data-testid="ownership-reviewed-badge"
      >
        <span className={styles.seal} aria-hidden="true">✓</span>
        <span>{BADGE_LABEL}</span>
        <span className={styles.info} aria-hidden="true">i</span>
      </button>
      {popover}
    </>
  );
}
